import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { launchPayment } from "@/lib/payments";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

const fmt = new Intl.NumberFormat("en-IN");

const SLOTS = ["10:00–11:00", "12:00–13:00", "15:00–16:00", "17:00–18:00"];
const today = () => new Date().toISOString().split("T")[0];

export const ConsultationBooking = ({ defaultName = "", defaultEmail = "", defaultPhone = "" }) => {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [prefDate, setPrefDate] = useState("");
  const [prefSlot, setPrefSlot] = useState(SLOTS[0]);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => { setConfig(r.data); setAmount(r.data.amounts?.[0] ?? null); }).catch(() => setConfig({ amounts: [] }));
  }, []);

  useEffect(() => { setName(defaultName); }, [defaultName]);
  useEffect(() => { setEmail(defaultEmail); }, [defaultEmail]);
  useEffect(() => { setPhone(defaultPhone); }, [defaultPhone]);

  const chosenAmount = amount === "custom" ? Number(custom) : Number(amount);
  const amountValid = chosenAmount >= (config?.min ?? 100) && chosenAmount <= (config?.max ?? 500000);

  const pay = async (e) => {
    e.preventDefault();
    if (!amountValid) { toast.error(`Enter an amount between ₹${fmt.format(config?.min ?? 100)} and ₹${fmt.format(config?.max ?? 500000)}.`); return; }
    setRedirecting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: chosenAmount, name, email, phone,
        preferred_date: prefDate || null, preferred_slot: prefSlot || null,
        origin_url: window.location.origin,
      });
      await launchPayment(data, { description: "Consultation booking", onCancel: () => setRedirecting(false) });
    } catch (err) {
      toast.error(formatApiError(err));
      setRedirecting(false);
    }
  };

  return (
    <form data-testid="consultation-form" onSubmit={pay} className="border border-slate-200 bg-[#F6F4FD]/70 p-8 backdrop-blur">
      <div className="flex items-center gap-3">
        <CalendarCheck className="h-5 w-5 text-[#6D28D9]" />
        <h3 className="font-display text-xl font-semibold tracking-tight">Book an advisory consultation</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        Pay a booking fee to lock in a one-on-one session with the desk on unlisted &amp; Pre-IPO opportunities. Choose a preset or enter your own amount.
      </p>

      <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Select amount (₹)</p>
      <div className="mt-3 flex flex-wrap gap-2.5">
        {(config?.amounts || []).map((a) => (
          <button
            key={a}
            type="button"
            data-testid={`consult-amount-${a}`}
            onClick={() => setAmount(a)}
            className={`border px-5 py-2.5 font-mono2 text-sm tracking-wide transition-colors ${amount === a ? "border-[#6D28D9] bg-[#6D28D9]/10 text-[#6D28D9]" : "border-slate-200 text-slate-700 hover:border-[#6D28D9]/40"}`}
          >
            ₹{fmt.format(a)}
          </button>
        ))}
        <button
          type="button"
          data-testid="consult-amount-custom"
          onClick={() => setAmount("custom")}
          className={`border px-5 py-2.5 font-mono2 text-sm tracking-wide transition-colors ${amount === "custom" ? "border-[#6D28D9] bg-[#6D28D9]/10 text-[#6D28D9]" : "border-slate-200 text-slate-700 hover:border-[#6D28D9]/40"}`}
        >
          Custom
        </button>
      </div>
      {amount === "custom" && (
        <input
          data-testid="consult-custom-input"
          type="number"
          min={config?.min ?? 100}
          max={config?.max ?? 500000}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder={`Amount in ₹ (min ${fmt.format(config?.min ?? 100)})`}
          className={`${inputCls} mt-3`}
        />
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <input data-testid="consult-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
        <input data-testid="consult-phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className={inputCls} />
      </div>
      <input data-testid="consult-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={`${inputCls} mt-3`} />

      <p className="mt-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Preferred slot (optional)</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <input
          data-testid="consult-date-input"
          type="date"
          min={today()}
          value={prefDate}
          onChange={(e) => setPrefDate(e.target.value)}
          className={`${inputCls} [color-scheme:dark]`}
        />
        <select data-testid="consult-slot-select" value={prefSlot} onChange={(e) => setPrefSlot(e.target.value)} className={inputCls}>
          {SLOTS.map((s) => <option key={s} value={s}>{`${s} IST`}</option>)}
        </select>
      </div>

      <button
        data-testid="consult-pay-button"
        type="submit"
        disabled={redirecting || !config}
        className="mt-6 flex w-full items-center justify-center gap-2 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors duration-300 hover:bg-[#5B21B6] disabled:opacity-50"
      >
        {redirecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to secure checkout…</> : <><Lock className="h-3.5 w-3.5" /> Pay ₹{fmt.format(amountValid ? chosenAmount : (config?.amounts?.[0] || 0))} securely</>}
      </button>
      <p className="mt-4 font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.15em] text-slate-400">
        Payments processed securely by Stripe. Booking fee is for advisory time only — not investment advice or a purchase of securities.
      </p>
    </form>
  );
};
